/**
 * ResendKilledDialog — re-send or re-schedule killed drops. SMSWiser cannot
 * restart a stopped drop, so each one goes out again as a NEW drop with the
 * same settings; the killed one stays in Killed, marked "Re-sent as #…".
 * Doubles as the confirmation: nothing is sent until the primary button.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useMemo, useState, type FC } from 'react';
import { CalendarClock, Send } from 'lucide-react';
import { nextOriginalResendTime } from '@trafficpulse/shared';

import { Button, LoadingSpinner, ModalCard } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { scheduleLocalToUtcIso, utcIsoToScheduleLocal } from '@/features/smswiser-campaigns/utils/scheduleTimezone';

import { planResend, plannedStartOf, type ResendMode } from '@/features/resend-killed-drops/planResend';
import type { ResendOutcome } from '@/features/resend-killed-drops/resendKilledApi';
import { useResendKilled } from '@/features/resend-killed-drops/useResendKilled';
import { ResendPlanList } from '@/features/resend-killed-drops/components/ResendPlanList';
import { ResendWhenPicker } from '@/features/resend-killed-drops/components/ResendWhenPicker';

export interface ResendKilledDialogProps {
  drops: PreparedDrop[];
  initialMode: ResendMode;
  onClose: () => void;
  /** Every drop went — the caller clears its selection. */
  onDone?: () => void;
}

/** The window moves with the clock; re-plan every 30 s while open. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** Pick mode opens on the first drop's own next planned time — a sane start. */
function defaultPick(drops: PreparedDrop[], now: Date): string {
  const planned = drops.map(plannedStartOf).find(Boolean) ?? new Date(now.getTime() + 3_600_000);
  return utcIsoToScheduleLocal(nextOriginalResendTime(planned, now).toISOString());
}

export const ResendKilledDialog: FC<ResendKilledDialogProps> = ({ drops, initialMode, onClose, onDone }) => {
  const now = useNow();
  const { run, running } = useResendKilled();
  const [mode, setMode] = useState<ResendMode>(initialMode);
  const [picked, setPicked] = useState(() => defaultPick(drops, new Date()));
  const [outcomes, setOutcomes] = useState<Map<string, ResendOutcome> | null>(null);
  const plan = useMemo(() => {
    const pickedAt = picked ? new Date(scheduleLocalToUtcIso(picked)) : null;
    return planResend(drops, mode, pickedAt, now);
  }, [drops, mode, picked, now]);
  const count = plan.ready.length;
  const canRun = count > 0 && !plan.issue && !running && !outcomes;

  const submit = async () => {
    const result = await run(plan.ready);
    if (result && result.failed === 0 && result.skipped === 0) {
      onDone?.();
      onClose();
      return;
    }
    if (result) setOutcomes(new Map(result.results.map((r) => [r.sourceId, r])));
  };

  const what = `${count} drop${count === 1 ? '' : 's'}`;
  const label = mode === 'now' ? `Re-send ${what} now` : `Schedule ${what}`;
  return (
    <ModalCard
      open
      title={mode === 'now' ? 'Re-send killed drops' : 'Re-schedule killed drops'}
      subtitle="SMSWiser can't restart a killed drop, so each one goes out again as a new drop with the same gateway, leads, amount, TLD, options, senders and texts. The killed drop stays in Killed, marked as re-sent."
      widthClass="max-w-2xl"
      busy={running}
      onClose={onClose}
    >
      <div className="space-y-4">
        <ResendWhenPicker
          mode={mode}
          onModeChange={setMode}
          picked={picked}
          onPickedChange={setPicked}
          issue={plan.issue}
          now={now}
          disabled={running || !!outcomes}
        />
        <ResendPlanList plan={plan} outcomes={outcomes ?? undefined} />
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            {outcomes
              ? 'Done — see each drop above. Undo last change stops the new drops.'
              : `${plan.ready.reduce((s, r) => s + r.drop.amount, 0).toLocaleString('en-US')} SMS in total · sends to real recipients`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={running}>
              {outcomes ? 'Close' : 'Cancel'}
            </Button>
            {!outcomes && (
              <Button size="sm" className="gap-2" disabled={!canRun} onClick={() => void submit()}>
                {running ? <LoadingSpinner size="xs" /> : mode === 'now' ? <Send className="h-4 w-4" /> : <CalendarClock className="h-4 w-4" />}
                {running ? 'Re-sending…' : label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalCard>
  );
};
